/**
 * FONTE ÚNICA DA VERDADE da geometria de card no JS do app de TV.
 *
 * ===== REGRA DURA: É PROIBIDO CRAVAR px DE CARD EM JS =====
 * A geometria do card vive no CSS (`--card-vod-w`, `--card-vod-poster-h`,
 * `--card-episode-w`, `--space-3`, ... em apps/tv/src/index.css; ver
 * docs/specs/ux-tv.md §5B.11). TODO número de geometria que o JS precisar sai
 * DAQUI, derivado do token computado em runtime. Nenhum outro módulo pode
 * declarar `const CARD_W = 256` — nem "só pra estimar", nem "só pro tier vlow".
 * O teste `card-metrics.test.ts` VARRE o código e falha se alguém cravar de novo.
 *
 * ===== POR QUE (o bug que originou esta regra, duas vezes) =====
 * 1ª: o grid virtualizado tinha `CELL_W = 200` / `CELL_H = 340` cravados. Como o
 *    react-window posiciona por offset ABSOLUTO (`index * rowHeight`), célula
 *    menor que o card = linhas SOBREPOSTAS (o estouro não empurra).
 * 2ª (o app MORRENDO na TV LG, ~25s, 1 em 4 launches): duas branches irmãs que
 *    nunca se falaram. A da virtualização cravou o card ANTIGO de 180px em dois
 *    lugares — `POSTER_CSS_WIDTH = 180` (Poster.tsx) e `ROW_VISIBLE_CARDS = 10`
 *    com pitch 192 (rowWindow.ts) — enquanto a da densidade TV-first subiu o card
 *    pra 256px. Resultado: a janela deslizante dimensionada pra um card que não
 *    existe mais (22 cards montados onde ~16 bastam) e o Poster pedindo ao TMDB
 *    um tamanho de imagem descolado do tamanho REALMENTE rasterizado. Ninguém
 *    "quebrou" nada isolado — a combinação é que estourava a memória de raster.
 *
 * A parte que LÊ o DOM é fina e blindada; a MATEMÁTICA é pura e testável.
 */

import { getCapability } from '@app-iptv/device'

import {
  DESIGN_HEIGHT,
  DESIGN_WIDTH,
  isCanvasScaled,
  isTvPlatform,
} from '../packaged-tv-scale'

import probeStyles from './card-metrics-probe.module.css'

/** Fallbacks = os valores do `:root` de apps/tv/src/index.css (web). Usados se o
 *  DOM não existir (SSR/teste) ou se o token não resolver (Chr38 não tem custom
 *  properties: lá o CSS também cai nestes números, então o JS bate com o CSS).
 *
 *  ATENÇÃO: estes números são o ESPELHO do bloco `:root` do index.css — não são
 *  "a geometria". Se o `:root` mudar, estes mudam junto (o teste compara os dois
 *  lendo o CSS do disco e falha se divergirem). */
export const GEOMETRY_FALLBACK = {
  cardW: 180,
  posterH: 270,
  infoH: 50,
  gap: 12,
  epgRow: 76,
  episodeW: 280,
  recW: 160,
  gutterX: 32,
  qrSize: 220,
} as const

const FALLBACK = GEOMETRY_FALLBACK

/**
 * Geometria de célula do grid virtualizado de VOD, derivada da do card.
 * PURA — recebe os px já lidos, não toca no DOM. É o que os testes exercitam.
 *
 * A célula = card + o gap dos dois lados (o `padding: CELL_GAP` que o renderer
 * do react-window aplica em cada cell).
 *
 * web (180x270+50, gap 12): 204 x 344
 * TV  (256x384+88, gap 12): 280 x 496
 *
 * ATENÇÃO à "largura útil": quem manda em `cols` é `readGridBox()`, medido no
 * .gridContainer, que usa `padding: goteira - gap` dos dois lados. A largura
 * DISPONÍVEL (o content box) é `1944 - 2 x goteira`; a conta é
 * `floor(content / 280)`. MEDIDO com goteira 32: content = 1880 -> 6 colunas.
 *
 * `1944 - 2g` NUNCA foi o clientWidth (esse é 1920 fixo — clientWidth INCLUI
 * padding); é o content box. readGridBox devolvia o clientWidth achando que era
 * o content — ver o banner dela. Com o número honesto a faixa não muda: 6
 * colunas de goteira 12px (piso: padding zero) até 132px, teto de
 * `1944 - 2g >= 6 x 280`. Em g = 133 o content cai pra 1678 e a grade vira 5.
 * É por isso que 96 -> 48 -> 32 não mexeu na contagem de colunas da GRADE DE
 * PÔSTERES: os três estão dentro da faixa. (O reporte de "5 por linha" veio de
 * outra grade, a .categoryCardsGrid, que pinava a coluna em px.)
 */
export function computeVodCell(
  cardW: number,
  posterH: number,
  infoH: number,
  gap: number,
  focusRing: number,
): { w: number; h: number; gap: number } {
  const w = cardW + gap * 2
  // O card tem borda (o anel de foco, transparente em repouso) nos 2 lados.
  const h = posterH + infoH + focusRing * 2 + gap * 2
  return { w, h, gap }
}

/** Token de geometria -> classe-sonda (card-metrics-probe.module.css). Ver o
 *  banner grande desse arquivo: a sonda tem que usar uma CLASSE DE CSS REAL
 *  (que passa pelo achatamento de TV legacy), não `style.height = 'var(--x)'`
 *  inline (que NUNCA é tocado pelo achatamento e é sintaxe inválida no
 *  Chromium 38 — a sonda mediria os números do WEB numa TV renderizando os
 *  números da TV, JS e CSS divergindo). Conjunto fechado, INVENTÁRIO COMPLETO
 *  dos tokens que este arquivo lê.
 *
 *  ===== TOKEN NOVO AQUI É OBRIGATÓRIO, NÃO OPCIONAL =====
 *  Ausência NÃO é erro de compilação: `readPxToken` cai no
 *  `if (!probeClass) return fallback` e devolve o fallback do web, calado, pra
 *  sempre. Foi assim que `--qr-size` passou: o CSS ganhou 6 declarações de QR
 *  por tier e nenhuma delas jamais chegou ao JS — o gate desenhava 220px fixo
 *  em toda TV, em todo tier. `card-metrics.dom.test.ts` agora tranca isso com
 *  um controle que FALHA se um token sair deste mapa. */
const PROBE_CLASS_BY_TOKEN: Record<string, string | undefined> = {
  '--card-vod-w': probeStyles.cardVodW,
  '--card-vod-poster-h': probeStyles.cardVodPosterH,
  '--card-vod-info-h': probeStyles.cardVodInfoH,
  '--space-3': probeStyles.space3,
  '--focus-ring-width': probeStyles.focusRingWidth,
  '--epg-row-h': probeStyles.epgRowH,
  '--gutter-x': probeStyles.gutterX,
  '--card-rec-w': probeStyles.cardRecW,
  '--card-episode-w': probeStyles.cardEpisodeW,
  '--qr-size': probeStyles.qrSize,
}

/**
 * Lê um token de px e devolve o valor RESOLVIDO. Nunca lança; cai no fallback se
 * não resolver (sem DOM, token ausente, Chr38 sem custom properties — este
 * último caso não deveria mais acontecer pros 9 tokens do inventário acima,
 * que o build de TV achata via classe real; sobra como rede de segurança
 * genuína só pra um token FORA do inventário, caso algum dia apareça).
 *
 * ===== POR QUE VIA ELEMENTO-SONDA (classe), E NÃO getPropertyValue() =====
 * `getComputedStyle(root).getPropertyValue('--x')` devolve o token stream APÓS a
 * substituição de var(), mas SEM avaliar o calc(). Medido no Chrome:
 *   --card-vod-info-h: calc(var(--font-card-title) * 1.2 * 2 + var(--space-2) * 2)
 *   -> getPropertyValue() = "calc(30px * 1.2 * 2 + 8px * 2)"  -> parseFloat = NaN
 * Ou seja: o caminho ingênuo cairia SILENCIOSAMENTE no fallback do web (50px) e o
 * react-window montaria a célula da TV com a altura do web — os cards
 * SOBREPORIAM. É exatamente a classe de bug que este módulo existe pra impedir.
 *
 * Aplicando o token numa PROPRIEDADE REAL (`height`) de um elemento VIA CLASSE
 * (não `style.height` inline — ver `PROBE_CLASS_BY_TOKEN` acima), o valor
 * computado é sempre px absoluto — o browser avalia o calc() por definição, E
 * o achatamento de TV legacy processa a declaração normalmente (bucket A/B),
 * incluindo a multiplicação de seletor por `html[data-tv-scale]`.
 *
 * Token FORA do inventário fechado (sem classe-sonda mapeada): devolve o
 * fallback DIRETO — sem tentar `style.height = var(name)` inline. Essa
 * tentativa EXISTIA antes (rede de segurança "genérica"), mas é exatamente o
 * padrão que este achatamento inteiro existe pra eliminar: style inline com
 * `var()` nunca passa pelo pipeline de CSS e é sintaxe INVÁLIDA no Chromium
 * 38 (achado pelo gate `check-emitted-css.mjs` — a última ocorrência de
 * `var(` sobrevivendo no bundle era esta linha). Token novo = entrada nova
 * em `PROBE_CLASS_BY_TOKEN` + classe nova em
 * `card-metrics-probe.module.css`, não um atalho inline.
 *
 * ===== MEMOIZADO (o custo do elemento-sonda) =====
 * A sonda faz `appendChild` + `getComputedStyle` = REFLOW SÍNCRONO forçado. O
 * ScrollableRow chama isto 2x na fase de render de CADA trilho: na Home (~20
 * trilhos) eram ~60 layouts síncronos no caminho mais quente do app — numa TV de
 * 2017 isso é frame perdido puro. Os tokens são CONSTANTES na sessão (o viewport
 * de TV é travado), com UMA exceção: `--card-vod-info-h` deriva de `--font-scale`
 * (acessibilidade). Por isso o cache é invalidado por `resetTokenCache()`, chamado
 * de onde o --font-scale é aplicado (settings-store).
 */
const _tokenCache = new Map<string, number>()

export function readPxToken(name: string, fallback: number): number {
  const cached = _tokenCache.get(name)
  if (cached !== undefined) return cached
  try {
    if (typeof document === 'undefined' || !document.body) return fallback
    const probeClass = PROBE_CLASS_BY_TOKEN[name]
    if (!probeClass) return fallback
    const probe = document.createElement('div')
    probe.style.position = 'absolute'
    probe.style.visibility = 'hidden'
    probe.style.pointerEvents = 'none'
    probe.className = probeClass
    document.body.appendChild(probe)
    const px = Number.parseFloat(getComputedStyle(probe).height)
    document.body.removeChild(probe)
    if (!Number.isFinite(px) || px <= 0) return fallback
    _tokenCache.set(name, px)
    return px
  } catch {
    return fallback
  }
}

/**
 * Invalida o cache de tokens. Chamar SEMPRE que uma custom property que alimenta
 * a geometria mudar em runtime — hoje só `--font-scale` (settings-store), da qual
 * `--card-vod-info-h` deriva. Sem isto, trocar a fonte pra "xlarge" manteria a
 * caixa de info do card com a altura da fonte antiga → título CORTADO / linhas
 * sobrepostas na próxima montagem do grid.
 *
 * (O fallback NUNCA é cacheado — só valor que resolveu de verdade. Assim, um
 * `readPxToken` chamado antes do CSS existir não congela o fallback pra sessão.)
 */
export function resetTokenCache(): void {
  _tokenCache.clear()
}

/**
 * Geometria da célula do grid, lida dos tokens vigentes. Chamar DEPOIS do boot
 * (o gate `html[data-tv-scale]` é marcado em applyTvUiScale()) — na prática,
 * dentro de um `useMemo` do componente, nunca em const de módulo (que avaliaria
 * antes do boot).
 */
export function readVodCell(): { w: number; h: number; gap: number } {
  return computeVodCell(
    readPxToken('--card-vod-w', FALLBACK.cardW),
    readPxToken('--card-vod-poster-h', FALLBACK.posterH),
    readPxToken('--card-vod-info-h', FALLBACK.infoH),
    readPxToken('--space-3', FALLBACK.gap),
    readPxToken('--focus-ring-width', 3),
  )
}

/**
 * Altura da linha do guia de EPG (`itemSize` do react-window).
 * DEVE bater com `.row` do EpgScreen.module.css — se divergir, a lista descola
 * do CSS e as linhas sobrepõem.
 */
export function readEpgRowHeight(): number {
  return readPxToken('--epg-row-h', FALLBACK.epgRow)
}

/**
 * Lado (px) do QR do gate de conta. O componente de QR recebe `size` como NUMBER
 * (desenha num canvas/svg), então este é um dos poucos px que o JS precisa saber.
 *
 * Vinha de um switch de tier em JS multiplicado por `getUiScale()` — e essa era a
 * ÚNICA razão de o --ui-scale ainda ser lido por JS. Com a base de TV rebaseada
 * (o multiplicador morreu), o tamanho vira TOKEN: `--qr-size` varia por tier
 * (`html[data-tier=...]`) e por canvas (`html[data-tv-scale]`, TV @1920) direto no
 * index.css. Mesma disciplina do resto da geometria: o número mora no CSS, o JS
 * deriva.
 */
export function readQrSize(): number {
  return readPxToken('--qr-size', FALLBACK.qrSize)
}

// ===========================================================================
// GRADE VERTICAL (react-window) — colunas DERIVADAS, não cravadas
// ===========================================================================

/**
 * Quantas colunas cabem. PURA.
 *
 * Era `MIN_COLS = 6` cravado nos 3 drills (`gridSize.w <= 1920 ? MIN_COLS : ...`),
 * um número que só valia pro card de 180px do web e pro 256px SE o gap fosse
 * exatamente o do desenho. Com a célula real medida em runtime, 6 colunas podem
 * simplesmente NÃO CABER em 1728px úteis — e como o react-window posiciona por
 * offset absoluto, a coluna que não cabe é CORTADA pela borda da safe area
 * (card clipado = card não navegável em TV).
 *
 * Regra: quantas células INTEIRAS cabem na largura disponível. Piso 1 (nunca
 * devolve 0 — `columnCount={0}` faz o react-window renderizar grid vazio).
 */
export function computeGridCols(availableWidth: number, cellW: number): number {
  if (!Number.isFinite(availableWidth) || !Number.isFinite(cellW) || cellW <= 0) return 1
  return Math.max(1, Math.floor(availableWidth / cellW))
}

/**
 * Largura do CANVAS em que o conteúdo é desenhado. PURA (recebe o que mediu).
 *
 * ===== O BUG DA TÁTICA B (o P0 do review) =====
 * Quando o firmware ignora a meta viewport, o boot fixa o #root no canvas de
 * DESIGN (1920px) e o encolhe com `transform: scale(innerWidth/1920)` — ver
 * index.css / packaged-tv-scale.ts. Nesse modo `window.innerWidth` devolve o
 * viewport REAL (1280, 1024, 960...), NÃO o canvas em que o layout acontece.
 * Dimensionar a grade por innerWidth ali é dimensionar pelo número errado: a
 * grade nasce estreita (4 colunas em vez de 6) e o ResizeObserver não salva —
 * transform não muda tamanho de LAYOUT, então ele nem re-dispara.
 *
 * Regra: sob a tática B, o canvas é o DESIGN_WIDTH. Fora dela, é o viewport.
 */
export function computeCanvasWidth(innerWidth: number, canvasScaled: boolean): number {
  if (canvasScaled) return DESIGN_WIDTH
  return Number.isFinite(innerWidth) && innerWidth > 0 ? innerWidth : DESIGN_WIDTH
}

/** Idem pra altura (o `#root` da tática B é 1920x1080 fixo). */
export function computeCanvasHeight(innerHeight: number, canvasScaled: boolean): number {
  if (canvasScaled) return DESIGN_HEIGHT
  return Number.isFinite(innerHeight) && innerHeight > 0 ? innerHeight : DESIGN_HEIGHT
}

/** Altura do canvas vigente (viewport real, ou o design sob a tática B). */
export function readCanvasHeight(): number {
  const ih = typeof window !== 'undefined' ? window.innerHeight : 0
  return computeCanvasHeight(ih, isCanvasScaled())
}

/** Largura útil de conteúdo: canvas menos a goteira (`--gutter-x`, que na TV
 *  vale a safe area de overscan). Chute INICIAL do grid antes de o container
 *  existir pra medir — derivado do token, nunca de um `MIN_COLS * CELL_W`
 *  cravado. Imune à tática B via computeCanvasWidth (ver acima). */
export function readContentWidth(): number {
  const iw = typeof window !== 'undefined' ? window.innerWidth : 0
  const canvas = computeCanvasWidth(iw, isCanvasScaled())
  const gutter = readPxToken('--gutter-x', FALLBACK.gutterX)
  return Math.max(0, canvas - gutter * 2)
}

/** Espaço vertical que o CROMO da tela (header + filtros + goteira) consome
 *  acima do grid. Usado SÓ no fallback de altura, quando o container ainda não
 *  tem caixa de layout (1º render). Chute grosseiro de propósito: assim que o
 *  container mede, o valor real manda. */
const GRID_CHROME_H = 200

/** Padding horizontal (px de LAYOUT) de um elemento. `getComputedStyle` devolve
 *  padding já resolvido em px absolutos e é imune a transform — mesma régua do
 *  clientWidth de quem chama. Nunca lança: padding que não resolve vira 0, que é
 *  o pior caso seguro (devolve a caixa inteira, como era antes). */
function readPaddingX(el: HTMLElement): number {
  try {
    const cs = getComputedStyle(el)
    const l = Number.parseFloat(cs.paddingLeft)
    const r = Number.parseFloat(cs.paddingRight)
    return (Number.isFinite(l) ? l : 0) + (Number.isFinite(r) ? r : 0)
  } catch {
    return 0
  }
}

/**
 * Caixa de LAYOUT do container do grid — a métrica que o react-window consome.
 *
 * ===== POR QUE clientWidth E NÃO getBoundingClientRect() (o P0 do review) =====
 * `getBoundingClientRect()` devolve px VISUAIS: sob o `transform: scale(0.667)`
 * da tática B, um container de 1728px de LAYOUT mede 1152. `floor(1152/280)` = 4
 * colunas em vez de 6 → a grade nasce com 1152 de 1728 e ~1/3 da tela fica VAZIO.
 * Pior: como transform não altera tamanho de layout, o ResizeObserver não
 * re-dispara e a grade fica PRESA assim.
 * `clientWidth`/`offsetWidth` são métricas de LAYOUT — imunes a transform. Prova
 * de que é a métrica certa: o ScrollableRow nunca teve esse bug JUSTAMENTE porque
 * já mede com `clientWidth`/`offsetLeft`.
 *
 * Cadeia de fallback: clientWidth (layout, sem borda/scrollbar) → offsetWidth
 * (layout, com borda) → readContentWidth() (token). `rect` NUNCA entra.
 *
 * ===== POR QUE DESCONTAR O PADDING NA MÃO (a conta que estava errada) =====
 * `clientWidth` INCLUI o padding — ele tira borda e scrollbar, não padding. Este
 * módulo afirmava o contrário ("clientWidth já desconta padding", "clientWidth =
 * 1944 - 2 x goteira"), e o .gridContainer tem `padding: 0 calc(var(--gutter-x)
 * - var(--space-3))` pra pousar o 1º card na goteira. MEDIDO no gate
 * `probe:gutter` (1920x1080, --safe-x=32, padding 20px/lado):
 *   clientWidth = 1920  (NÃO 1880 — o padding está DENTRO)
 *   content box = 1880  ( = 1944 - 2*goteira, a fórmula que o comentário tinha,
 *                         só que ela sempre descreveu o CONTENT, não o client)
 * Ou seja: `1944 - 2g` estava certo, o rótulo é que estava trocado — e a função
 * devolvia clientWidth (1920), um número 40px MAIOR que a caixa onde o grid
 * realmente cabe. O react-window recebia `width={1920}` num container de 1880 e
 * o flex-shrink encolhia o outer em silêncio: a largura declarada era ficção.
 *
 * Hoje isso não muda a contagem (floor(1920/280) e floor(1880/280) dão 6 os
 * dois), o que é exatamente por que o bug passou despercebido. Mas é sorte de
 * arredondamento, e ela acaba: a coluna é CORTADA quando cols*280 > content, ou
 * seja quando 1680 > 1944 - 2g → g > 132. Com o número honesto a grade nunca
 * pede mais largura do que tem, e `computeGridColAt` (que já mede a partir do
 * CONTEÚDO, via readLayoutOffsetX) passa a falar a mesma régua que ela.
 */
export function readGridBox(el: HTMLElement, cellH: number): { w: number; h: number } {
  const rawH = el.clientHeight || el.offsetHeight || 0
  const box = el.clientWidth || el.offsetWidth || 0
  const rawW = box > 0 ? Math.max(0, box - readPaddingX(el)) : 0
  const w = rawW > 0 ? rawW : readContentWidth()
  // Container ainda sem altura de layout (1º render): estima pelo canvas.
  const h = rawH > cellH ? rawH : readCanvasHeight() - GRID_CHROME_H
  return { w, h: Math.max(h, cellH * 2) }
}

/**
 * Converte um X de VIEWPORT (px visuais) no deslocamento dentro do CONTEÚDO de
 * um container, em px de LAYOUT — a unidade em que CELL_W vive.
 *
 * ===== POR QUE NÃO DÁ PRA SUBTRAIR RECT DE PADDING (a mesma armadilha do P0
 * do readGridBox, uma função acima) =====
 * `getBoundingClientRect()` devolve px VISUAIS: sob o `transform: scale(0.667)`
 * da tática B (firmware que ignora a meta viewport), um container de 1728px de
 * LAYOUT mede 1152. Já `clientLeft` e o `paddingLeft` do computed style são px
 * de LAYOUT — transform não os toca. Somar os dois e dividir por CELL_W (layout)
 * mistura as duas réguas: com s = 0.667, um chip em X = 1100 resolvia pra coluna
 * 3 em vez de 5. Fora de 1920 é caminho VIVO em device, não hipótese.
 *
 * A escala sai do próprio elemento (rect / offsetWidth), então não precisamos
 * saber quem aplicou o transform nem ler o token: (X - rect.left) é visual e
 * vira layout ao dividir por ela; só depois descontamos borda e padding, que já
 * são de layout. Com s = 1 a conta é idêntica à de antes.
 */
export function readLayoutOffsetX(el: HTMLElement, viewportX: number): number {
  const rect = el.getBoundingClientRect()
  const layoutW = el.offsetWidth
  const scale = rect.width > 0 && layoutW > 0 ? rect.width / layoutW : 1
  const padLeft = Number.parseFloat(getComputedStyle(el).paddingLeft)
  const visualOffset = viewportX - rect.left
  return visualOffset / scale - el.clientLeft - (Number.isFinite(padLeft) ? padLeft : 0)
}

/**
 * Coluna do grid sob um deslocamento X — é o que decide em qual card o foco
 * pousa ao descer o D-pad de um chip do rail.
 * PURA — recebe os px já lidos, não toca no DOM.
 *
 * `offsetInContent` TEM que ser medido a partir do CONTEÚDO do .gridContainer e
 * em px de LAYOUT (ver readLayoutOffsetX): o container carrega
 * `padding: 0 calc(var(--gutter-x) - var(--space-3))` pra pousar o 1º card na
 * goteira. Medir do border-box devolveria a coluna deslocada de
 * (goteira - gap) px — na prática o foco caindo um card à direita do chip.
 */
export function computeGridColAt(
  offsetInContent: number,
  cellW: number,
  cols: number,
): number {
  if (cols <= 0 || cellW <= 0) return 0
  return Math.min(cols - 1, Math.max(0, Math.floor(offsetInContent / cellW)))
}

// ===========================================================================
// TRILHO HORIZONTAL — pitch/visíveis DERIVADOS (ver rowWindow.ts pra a janela)
// ===========================================================================

/** Geometria do trilho lida dos tokens vigentes. `gap` é o `--space-3` do
 *  `.items > * + *` de ScrollableRow.module.css — o MESMO token, senão o pitch
 *  do JS mente sobre o layout do CSS.
 *
 *  É o chute do PRIMEIRO render (antes de existir card no DOM pra medir). O
 *  ScrollableRow refina com a medição REAL de 2 cards adjacentes assim que eles
 *  montam — token e DOM concordam por construção; a medição é a rede de
 *  segurança contra qualquer margem que o CSS acrescente por fora do token. */
export function readRowGeometry(): { cardW: number; gap: number } {
  return {
    cardW: readPxToken('--card-vod-w', FALLBACK.cardW),
    gap: readPxToken('--space-3', FALLBACK.gap),
  }
}

// ===========================================================================
// TAMANHO DE IMAGEM TMDB — derivado da largura REAL do card × DPR ÚTIL
// ===========================================================================

/** Variantes TMDB de PÔSTER, da menor pra maior (lista oficial; ver
 *  packages/shared/src/tmdb.ts). w92 fica de fora: ilegível em qualquer card. */
export const POSTER_SIZE_LADDER: ReadonlyArray<readonly [number, string]> = [
  [154, 'w154'],
  [185, 'w185'],
  [342, 'w342'],
  [500, 'w500'],
]

/**
 * DPR ÚTIL — o que de fato aparece na tela. PURA.
 *
 * Na TV o plano de UI é composto de volta pra FHD pelo firmware: o webOS real
 * reporta `devicePixelRatio: 2` (rasteriza 3840x2160) mas o compositor entrega
 * 1920x1080 — medido e documentado em docs/specs/tv-compat.md §7.1, junto com as
 * 3 tentativas (appinfo/meta viewport/CDP) de baixar o DPR, todas negativas.
 * Consequência direta pra imagem: pedir ao TMDB mais pixels do que a largura CSS
 * do card é 100% custo de decode/raster e 0% de nitidez percebida — exatamente a
 * memória que estoura numa TV de 2017.
 *
 * Fora da TV (web/mobile), DPR é real (retina mostra o que decodifica) e vale
 * até 2 — acima disso vira desperdício (3x/4x não paga o decode).
 */
export function effectiveDpr(rawDpr: number, isTv: boolean): number {
  const dpr = Number.isFinite(rawDpr) && rawDpr > 0 ? rawDpr : 1
  if (isTv) return 1
  return Math.min(dpr, 2)
}

/**
 * Menor variante TMDB que COBRE a largura pedida, respeitando um teto. PURA.
 *
 * 5% de tolerância pra não subir um degrau inteiro por arredondamento de DPR.
 * Se nada cobre (card gigante), devolve o teto — o teto é um limite de MEMÓRIA,
 * não uma preferência estética: quem manda é ele.
 */
export function pickPosterSize(neededPx: number, maxSize: string): string {
  const cap = POSTER_SIZE_LADDER.findIndex(([, name]) => name === maxSize)
  const ladder = cap >= 0 ? POSTER_SIZE_LADDER.slice(0, cap + 1) : POSTER_SIZE_LADDER
  const needed = Number.isFinite(neededPx) && neededPx > 0 ? neededPx : 0
  const match = ladder.find(([w]) => w >= needed * 0.95)
  return match ? match[1] : (ladder[ladder.length - 1]?.[1] ?? 'w500')
}

/** Teto de variante por tier. É um limite de MEMÓRIA DE RASTER, não de gosto:
 *  em vlow/low (Tizen/webOS 2017) cada degrau da escada dobra a área decodada
 *  mantida em cache. w342 cobre o card de 256px da TV com folga; w500 seria 2x a
 *  área pra zero pixel a mais na tela (o plano de UI é FHD — ver effectiveDpr). */
function posterMaxSizeForTier(): string {
  const bucket = getCapability('swImageCacheSuffix')
  return bucket.includes('vlow') || bucket.includes('low') ? 'w342' : 'w500'
}

/** Largura em px de DEVICE que um card de `token` ocupa (CSS × DPR útil). */
function deviceWidthOf(token: string, fallback: number): number {
  const cardW = readPxToken(token, fallback)
  const rawDpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
  return cardW * effectiveDpr(rawDpr, isTvPlatform())
}

/**
 * Tamanho TMDB do pôster do card de VOD, DERIVADO da largura real do card.
 *
 * Antes: `POSTER_CSS_WIDTH = 180` cravado (com um comentário jurando que "não é
 * estimativa") + um short-circuit que forçava `w154` em vlow. Com o card em
 * 256px, o w154 virou um upscale de 1.66x — pôster BORRADO na TV — e o 180
 * virou ficção. Agora: largura computada do card × DPR útil, na escada, com teto
 * por tier.
 *
 * Cacheado module-level: tier, DPR e largura de card não mudam durante a sessão
 * numa TV (viewport travado). `resetImageSizeCache()` existe pro teste.
 */
let _cachedPosterSize: string | null = null
export function readPosterSize(): string {
  if (_cachedPosterSize !== null) return _cachedPosterSize
  _cachedPosterSize = pickPosterSize(
    deviceWidthOf('--card-vod-w', FALLBACK.cardW),
    posterMaxSizeForTier(),
  )
  return _cachedPosterSize
}

/** Tamanho TMDB do pôster do trilho de RECOMENDAÇÕES, que tem card próprio
 *  (`--card-rec-w`, 160px) e NÃO acompanhou o rebase de densidade. Sem este
 *  caminho separado, o card de 160px receberia a imagem do card de 256px — 4.5x
 *  de área decodada à toa, e na HOME, que é onde a memória aperta. */
let _cachedRecSize: string | null = null
export function readRecPosterSize(): string {
  if (_cachedRecSize !== null) return _cachedRecSize
  _cachedRecSize = pickPosterSize(
    deviceWidthOf('--card-rec-w', FALLBACK.recW),
    posterMaxSizeForTier(),
  )
  return _cachedRecSize
}

/** Variantes TMDB de STILL (frame de episódio). A escada oficial de still PARA em
 *  w300 — `original` é o próximo degrau e pode pesar megabytes. Então w300 é teto
 *  de contrato do TMDB, não número escolhido: o card de episódio da TV (340px)
 *  fica com 0.88x de resolução e é o máximo disponível. */
const STILL_SIZE_LADDER: ReadonlyArray<readonly [number, string]> = [
  [92, 'w92'],
  [185, 'w185'],
  [300, 'w300'],
]

/** Tamanho TMDB da thumb 16:9 (EpisodeCard/EPG), derivado de `--card-episode-w`.
 *  Mesmo princípio do pôster; escada e teto são os do STILL. */
let _cachedThumbSize: string | null = null
export function readThumbSize(): string {
  if (_cachedThumbSize !== null) return _cachedThumbSize
  const needed = deviceWidthOf('--card-episode-w', FALLBACK.episodeW)
  const match = STILL_SIZE_LADDER.find(([w]) => w >= needed * 0.95)
  _cachedThumbSize = match ? match[1] : 'w300'
  return _cachedThumbSize
}

/** Zera os caches de tamanho de imagem. SÓ pra teste (cada caso monta um DOM /
 *  DPR diferente e precisa reavaliar). */
export function resetImageSizeCache(): void {
  _cachedPosterSize = null
  _cachedRecSize = null
  _cachedThumbSize = null
}
